import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AccountSummary } from '../api/types'
import type { ConnectedPlatform, ConnectionState, LocalUser } from '../api/auth'
import type { PlatformCapabilities } from '../api/platforms'
import { selectActiveAccount } from '../api/platforms'
import { setAccountArmed } from '../api/fleet'
import { toast } from '../toast/useToastStore'

type AuthStatus =
  | 'checking'
  | 'unauthenticated'
  | 'authenticating'
  | 'needs_platform'
  | 'connecting'
  | 'connected'
  | 'error'

interface AuthState {
  user: LocalUser | null
  isAuthenticated: boolean
  vaultUnlocked: boolean
  platforms: ConnectedPlatform[]
  capabilities: PlatformCapabilities | null
  accounts: AccountSummary[]
  accountId: string | null
  scopeAccountId: string | null
  status: AuthStatus
  errorMessage: string | null
  skippedPlatformAttach: boolean

  setSession: (params: {
    user: LocalUser | null
    isAuthenticated: boolean
    vaultUnlocked: boolean
  }) => void
  setConnection: (connection: ConnectionState | null) => void
  setAccounts: (accounts: AccountSummary[]) => void
  setAccountId: (accountId: string) => Promise<void>
  setScope: (accountId: string | null) => void
  setArmed: (accountId: string, armed: boolean) => Promise<void>
  setStatus: (status: AuthStatus, errorMessage?: string | null) => void
  setSkippedPlatformAttach: (skipped: boolean) => void
  clearConnection: () => void
  reset: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      vaultUnlocked: false,
      platforms: [],
      capabilities: null,
      accounts: [],
      accountId: null,
      scopeAccountId: null,
      status: 'checking',
      errorMessage: null,
      skippedPlatformAttach: false,

      setSession: ({ user, isAuthenticated, vaultUnlocked }) =>
        set({ user, isAuthenticated, vaultUnlocked }),
      setConnection: (connection) =>
        set({
          platforms: connection?.platforms ?? [],
          capabilities: connection?.capabilities ?? null,
          accounts: connection?.accounts ?? [],
          accountId: connection?.accountId ?? null,
        }),
      setAccounts: (accounts) => set({ accounts }),
      setScope: (accountId) => set({ scopeAccountId: accountId }),
      setArmed: async (accountId, armed) => {
        // Optimistic update of the accounts array
        set((s) => ({
          accounts: s.accounts.map(a =>
            a.accountId === accountId ? { ...a, armed } : a
          ),
        }))
        try {
          await setAccountArmed(accountId, armed)
        } catch (err) {
          // Roll back on failure
          set((s) => ({
            accounts: s.accounts.map(a =>
              a.accountId === accountId ? { ...a, armed: !armed } : a
            ),
          }))
          throw err
        }
      },
      setAccountId: async (accountId) => {
        set({ accountId })
        try {
          const connection = await selectActiveAccount(accountId)
          set({
            platforms: connection.platforms,
            capabilities: connection.capabilities,
            accounts: connection.accounts,
            accountId: connection.accountId,
          })
        } catch {
          // Local state already updated optimistically; the server still has
          // the previous active account — tell the user instead of failing silently.
          toast.error('Account switch didn’t reach the server — balances may show the previous account.')
        }
      },
      setStatus: (status, errorMessage = null) => set({ status, errorMessage }),
      setSkippedPlatformAttach: (skipped) => set({ skippedPlatformAttach: skipped }),
      clearConnection: () =>
        set({
          platforms: [],
          capabilities: null,
          accounts: [],
          accountId: null,
        }),
      reset: () =>
        set({
          user: null,
          isAuthenticated: false,
          vaultUnlocked: false,
          platforms: [],
          capabilities: null,
          accounts: [],
          accountId: null,
          status: 'unauthenticated',
          errorMessage: null,
          skippedPlatformAttach: false,
        }),
    }),
    {
      name: 'public-trading-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        platforms: state.platforms,
        capabilities: state.capabilities,
        accounts: state.accounts,
        accountId: state.accountId,
        skippedPlatformAttach: state.skippedPlatformAttach,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) state.status = 'checking'
      },
    },
  ),
)
